import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastAdvertencia, toastErrorApi, toastExito } from "@/lib/toast";
import {
  crearCentroDeCosto,
  crearCuentaContable,
  editarCentroDeCosto,
  editarCuentaContable,
  importarCentrosDeCosto,
  importarCuentasContables,
  type CrearCecoInput,
  type EditarCecoInput,
  type FilaImportacionItemCatalogo,
  type ResultadoImportacionItemCatalogo,
} from "../services/catalogos-contables-admin-service";

/** Toast unificado con el resumen de una importación (creados/actualizados/omitidos + errores). */
function reportarImportacion(res: ResultadoImportacionItemCatalogo, entidad: string) {
  const resumen = `${res.creados} creados · ${res.actualizados} actualizados · ${res.omitidos} omitidos`;
  if (res.errores.length > 0) {
    const muestra = res.errores.slice(0, 5).join(" · ");
    const extra = res.errores.length > 5 ? ` (+${res.errores.length - 5} más)` : "";
    toastAdvertencia({ title: `Importación de ${entidad} con avisos`, message: `${resumen}. ${muestra}${extra}` });
  } else {
    toastExito({ title: `Importación de ${entidad} completada`, message: resumen });
  }
}

/**
 * Las mutations de CeCo y Cuenta tienen contratos idénticos. Las
 * mantenemos como hooks separados (uno por entidad) en vez de un solo
 * hook parametrizado · es más legible en el componente.
 */
export function useCrearCeco(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearCecoInput) => crearCentroDeCosto(input),
    onSuccess: () => {
      toastExito({ title: "CeCo creado", message: "Disponible en el catálogo." });
      void qc.invalidateQueries({ queryKey: qk.cecos });
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEditarCeco(opts?: { onEditado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EditarCecoInput }) => editarCentroDeCosto(id, input),
    onSuccess: () => {
      toastExito({ title: "CeCo actualizado", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.cecos });
      opts?.onEditado?.();
    },
    onError: toastErrorApi,
  });
}

export function useCrearCuenta(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearCecoInput) => crearCuentaContable(input),
    onSuccess: () => {
      toastExito({ title: "Cuenta creada", message: "Disponible en el catálogo." });
      void qc.invalidateQueries({ queryKey: qk.cuentas });
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEditarCuenta(opts?: { onEditado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EditarCecoInput }) => editarCuentaContable(id, input),
    onSuccess: () => {
      toastExito({ title: "Cuenta actualizada", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.cuentas });
      opts?.onEditado?.();
    },
    onError: toastErrorApi,
  });
}

export function useImportarCecos(opts?: { onImportado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filas: FilaImportacionItemCatalogo[]) => importarCentrosDeCosto(filas),
    onSuccess: (res) => {
      reportarImportacion(res, "CeCos");
      void qc.invalidateQueries({ queryKey: qk.cecos });
      opts?.onImportado?.();
    },
    onError: toastErrorApi,
  });
}

export function useImportarCuentas(opts?: { onImportado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filas: FilaImportacionItemCatalogo[]) => importarCuentasContables(filas),
    onSuccess: (res) => {
      reportarImportacion(res, "cuentas");
      void qc.invalidateQueries({ queryKey: qk.cuentas });
      opts?.onImportado?.();
    },
    onError: toastErrorApi,
  });
}
